import { CheckCircleIcon, HomeIcon } from '@heroicons/react/24/outline';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';
import {
  Button,
  FormLabelWrapper,
  Heading,
  HelperTextRElement,
  Modal,
  ModalContent,
  Text,
} from '@restworld/ui-ds-base';
import { useAddressAdditionalFields, cn } from '@restworld/utils-common';
import { setWorkerAtom, workerAtom } from '../../../global-state.worker-data';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Marker } from 'react-map-gl/maplibre';
import React from 'react';
import { useWorkersService } from '@restworld/data-services';
import { Address, Worker } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';
import { useTranslation } from 'next-i18next';

export type HomeModalContentType = 'view' | 'success' | 'edit';

const showWorkerHomeLocationAtom = atom<boolean>(false);

const setShowWorkerHomeLocationAtom = atom(null, (_, set, show: boolean) => {
  set(showWorkerHomeLocationAtom, show);
});

const workerLocationAtom = atom((get) => {
  const worker = get(workerAtom);
  return {
    latitude: worker?.data?.address_lat,
    longitude: worker?.data?.address_lon,
  };
});

const workerAddressAtom = atom((get) => {
  const worker = get(workerAtom);
  return worker?.data?.address;
});

export type AddressForm = {
  address: Address;
};

export const WorkerHomeLocation = memo(() => {
  const setShow = useSetAtom(setShowWorkerHomeLocationAtom);
  const location = useAtomValue(workerLocationAtom);

  if (!location.latitude || !location.longitude) {
    return null;
  }

  return (
    <Marker
      latitude={location.latitude}
      longitude={location.longitude}
      onClick={(event) => {
        setShow(true);
      }}
      scale={100}
    >
      <StyledMarker>
        <HomeIcon className="text-black w-6 h-6" />
      </StyledMarker>
    </Marker>
  );
});

export const WorkerHomeLocationWrapper = memo(() => {
  const [contentType, setContentType] = useState<HomeModalContentType>('view');
  const show = useAtomValue(showWorkerHomeLocationAtom);
  const setShow = useSetAtom(setShowWorkerHomeLocationAtom);
  const methods = useForm<AddressForm>();

  const onClose = useCallback(() => {
    setShow(false);
    setTimeout(() => {
      setContentType('view');
    }, 300);
  }, [setShow]);

  const onHandleClickPrimaryAction = useCallback(() => {
    if (contentType === 'view') {
      setContentType('edit');
    }
    if (contentType === 'edit') {
      setContentType('success');
    }
    if (contentType === 'success') {
      onClose();
    }
  }, [contentType, onClose]);

  const onHandleClickSecondaryAction = useCallback(() => {
    onClose();
  }, [onClose]);
  const footer = (
    <FooterBlock
      contentType={contentType}
      onClickPrimaryAction={onHandleClickPrimaryAction}
      onClickSecondaryAction={onHandleClickSecondaryAction}
    />
  );

  const titleEl = useMemo(() => <ModalTitle contentType={contentType} />, [contentType]);
  const addressEl = useMemo(() => <AddressBlock contentType={contentType} />, [contentType]);
  const descriptionEl = useMemo(() => <DescriptionTextBlock contentType={contentType} />, [contentType]);
  const addressBlockEl = useMemo(() => {
    if (contentType === 'edit') {
      return <AddressEditBlock />;
    }
    return null;
  }, [contentType]);

  return (
    <FormProvider {...methods}>
      <Modal open={show} onOpenChange={(v) => !v && onClose()}>
        <ModalContent
          className="w-full md:max-w-[26rem] md:min-w-[22rem] min-h-[20rem]"
          title={titleEl}
          footer={footer}
        >
          <div className="flex flex-col gap-4 items-center justify-start w-full pt-2 pb-4">
            {descriptionEl}
            {addressEl}
            {addressBlockEl}
          </div>
        </ModalContent>
      </Modal>
    </FormProvider>
  );
});

export const StyledMarker = ({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cn(
      'flex flex-col items-center justify-center shadow-lg w-[32px] h-[35px] rounded-full bg-white border border-gray-300',
      "after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:-ml-[5px] after:border-[6px] after:border-solid after:[border-color:white_transparent_transparent_transparent]",
      className
    )}
    {...props}
  />
);

export const ModalTitle = ({ contentType }: { contentType: HomeModalContentType }) => {
  const { t } = useTranslation('job-explorer-page');
  const titleIcon = useMemo(() => {
    if (contentType === 'success') {
      return <CheckCircleIcon className="w-8 h-8 text-green-600" />;
    } else if (contentType === 'edit') {
      return null;
    } else {
      return <HomeIcon className="w-8 h-8 text-primary-800" />;
    }
  }, [contentType]);

  const titleContent = useMemo(() => {
    if (contentType === 'success') {
      return t('updated_home_address');
    } else if (contentType === 'edit') {
      return t('this_is_the_location_of_your_home');
    } else {
      return t('change_home_address');
    }
  }, [contentType, t]);
  return (
    <div className="flex flex-col gap-2 items-center justify-start w-full">
      {titleIcon}
      <Heading size={3} className="text-center">
        {titleContent}
      </Heading>
    </div>
  );
};

export const DescriptionTextBlock = ({ contentType }: { contentType: HomeModalContentType }) => {
  const { t } = useTranslation('job-explorer-page');
  const descriptionContent = useMemo(() => {
    if (contentType === 'view') {
      return t('update_your_address_from_your_profile');
    } else if (contentType === 'edit') {
      return t('address_will_update_in_the_profile');
    } else {
      return null;
    }
  }, [t, contentType]);

  return <Text align="center">{descriptionContent}</Text>;
};

export const AddressBlock = ({ contentType }: { contentType: HomeModalContentType }) => {
  const { t } = useTranslation('job-explorer-page');
  const address = useAtomValue(workerAddressAtom);
  if (contentType === 'success') return null;
  return (
    <div className="flex flex-col gap-1 items-start justify-start w-full">
      <Text weight="medium">{t('living_address')}</Text>
      <div className="flex items-center gap-1">
        <HomeIcon className="h-5 text-gray-400" />
        <Heading size={5} color="muted-foreground" weight="normal">
          {address}
        </Heading>
      </div>
    </div>
  );
};

export const FooterBlock = memo(
  ({
    contentType,
    onClickPrimaryAction,
    onClickSecondaryAction,
  }: {
    contentType: HomeModalContentType;
    onClickPrimaryAction: () => void;
    onClickSecondaryAction: () => void;
  }) => {
    const { t } = useTranslation();
    const methods = useFormContext();
    const workerService = useWorkersService();
    const onSetWorkerData = useSetAtom(setWorkerAtom);
    const auth = useAuth();
    const onSubmit = useCallback(
      async (data: { address: Partial<Worker> }) => {
        const response = await workerService.updateWorker({
          workerId: auth.user?.id,
          params: {
            ...data.address,
          },
        });
        onSetWorkerData({ ...response.data });
        onClickPrimaryAction();
      },
      [auth.user?.id, onClickPrimaryAction, onSetWorkerData, workerService]
    );
    const primaryActionText = useMemo(() => {
      if (contentType === 'view') return t('job-explorer-page:change_home_address');
      else if (contentType === 'edit') return t('job-explorer-page:save_new_address');
      else return t('common:close');
    }, [t, contentType]);

    const secondaryActionText = useMemo(() => {
      if (contentType === 'view') return t('job-explorer-page:all_clear');
      else if (contentType === 'edit') return t('common:cancel');
      else return null;
    }, [t, contentType]);
    return (
      <div className="flex flex-col w-full gap-2 items-center justify-start">
        {contentType === 'edit' && primaryActionText && (
          <form className="w-full" onSubmit={methods.handleSubmit(onSubmit)}>
            <Button fullwidth type="submit" loading={methods.formState.isSubmitting}>
              {primaryActionText}
            </Button>
          </form>
        )}
        {primaryActionText && contentType !== 'edit' && (
          <Button fullwidth onClick={onClickPrimaryAction}>
            {primaryActionText}
          </Button>
        )}
        {secondaryActionText && (
          <Button variant="secondary-neutral" fullwidth onClick={onClickSecondaryAction}>
            {secondaryActionText}
          </Button>
        )}
      </div>
    );
  }
);

export const AddressEditBlock = memo(() => {
  const [address, setAddress] = useState('');
  const methods = useFormContext();
  const errors = methods.formState.errors;
  const addressFields = useAddressAdditionalFields({ addressString: address, surface: 'worker_home_location_edit' });
  const setValue = methods.setValue;
  const { t } = useTranslation();
  React.useEffect(() => {
    if (addressFields && Object.keys(addressFields).length) {
      setValue('address', {
        address: addressFields.address,
        country: addressFields.country,
        address_lat: addressFields.address_lat,
        address_lon: addressFields.address_lon,
        administrative_area_level_1: addressFields.administrative_area_level_1,
        administrative_area_level_2: addressFields.administrative_area_level_2,
        postal_code: addressFields.postal_code,
        locality: addressFields.locality,
      });
    }
  }, [addressFields, setValue]);

  return (
    <FormLabelWrapper id={'address'} label={t('job-explorer-page:new_address')} isFullWidth>
      <GoogleAutoCompleteInput
        formId="address"
        trackingSurface="worker_home_location_edit"
        placeholder={t('job-explorer-page:enter_address')}
        required
        onSuggestionSelect={({ description }) => {
          setAddress(description);
        }}
        control={methods.control}
        key="address"
      />
      {Object.keys(errors).includes('address') && (
        <HelperTextRElement helperText={t('job-explorer-page:the_address_is_mandatory')} />
      )}
    </FormLabelWrapper>
  );
});
